/**
 * Singapore property rate constants.
 *
 * EVERY value here is copied from a primary government source. Never edit a
 * number without updating `source` and `verifiedOn` on the same block.
 *
 * Format for verifiedOn: DD/MM/YYYY.
 */

export interface DutyTier {
  /** Slice of the price this rate applies to. `null` means "everything above". */
  upTo: number | null;
  rate: number;
}

/**
 * Buyer's Stamp Duty, residential property.
 * Source: https://www.iras.gov.sg/quick-links/tax-rates/stamp-duty
 * Effective: on or after 15 Feb 2023. Verified on: 20/08/2026.
 */
export const BSD_RESIDENTIAL: DutyTier[] = [
  { upTo: 180_000, rate: 0.01 },
  { upTo: 180_000, rate: 0.02 },
  { upTo: 640_000, rate: 0.03 },
  { upTo: 500_000, rate: 0.04 },
  { upTo: 1_500_000, rate: 0.05 },
  { upTo: null, rate: 0.06 },
];

/**
 * Buyer's Stamp Duty, non-residential property.
 * Source: https://www.iras.gov.sg/quick-links/tax-rates/stamp-duty
 * Effective: on or after 15 Feb 2023. Verified on: 20/08/2026.
 */
export const BSD_NON_RESIDENTIAL: DutyTier[] = [
  { upTo: 180_000, rate: 0.01 },
  { upTo: 180_000, rate: 0.02 },
  { upTo: 640_000, rate: 0.03 },
  { upTo: 500_000, rate: 0.04 },
  { upTo: null, rate: 0.05 },
];

export type BuyerProfile =
  | "SC_1"
  | "SC_2"
  | "SC_3_PLUS"
  | "SPR_1"
  | "SPR_2"
  | "SPR_3_PLUS"
  | "FOREIGNER"
  | "ENTITY"
  | "TRUSTEE";

/**
 * Additional Buyer's Stamp Duty, residential property.
 * Source: https://www.iras.gov.sg/quick-links/tax-rates/stamp-duty
 * Effective: on or after 27 Apr 2023. Verified on: 20/08/2026.
 */
export const ABSD_RATES: Record<BuyerProfile, number> = {
  SC_1: 0,
  SC_2: 0.2,
  SC_3_PLUS: 0.3,
  SPR_1: 0.05,
  SPR_2: 0.3,
  SPR_3_PLUS: 0.35,
  FOREIGNER: 0.6,
  ENTITY: 0.65,
  TRUSTEE: 0.65,
};

export const ABSD_PROFILE_LABELS: Record<BuyerProfile, string> = {
  SC_1: "Singapore Citizen, 1st property",
  SC_2: "Singapore Citizen, 2nd property",
  SC_3_PLUS: "Singapore Citizen, 3rd property or more",
  SPR_1: "Permanent Resident, 1st property",
  SPR_2: "Permanent Resident, 2nd property",
  SPR_3_PLUS: "Permanent Resident, 3rd property or more",
  FOREIGNER: "Foreigner, any property",
  ENTITY: "Entity, any property",
  TRUSTEE: "Trustee, any property",
};

/**
 * Calculator UI defaults. These are starting values for the form, NOT legal
 * limits. Actual loan-to-value caps and tenure caps depend on the borrower's
 * age, existing loans and property type, so the calculator asks the user
 * rather than deciding for them. See the MAS and HDB links below.
 */
export const CALCULATOR_DEFAULTS = {
  propertyPrice: 1_500_000,
  loanPercent: 75,
  interestRate: 3,
  tenureYears: 25,
} as const;

/**
 * HDB concessionary housing loan terms.
 *
 * rate      - pegged at 0.10% above the prevailing CPF Ordinary Account rate.
 *             2.60% p.a. for both 1 Apr-30 Jun 2026 and 1 Jul-30 Sep 2026.
 *             Source: https://www.hdb.gov.sg/managing-my-home/finances/loan-matters/interest-rate
 * ltv       - 75% of the purchase price (new flat) or of the lower of resale
 *             price and value (resale). Applies to resale applications received
 *             on or after 20 Aug 2024.
 * tenure    - shortest of 25 years, 65 minus the average age of applicants, or
 *             the remaining lease minus 20 years. The calculator can only apply
 *             the 25-year cap; the other two depend on the buyer.
 * msr       - monthly instalments up to 30% of the applicants' monthly income.
 *             Source: https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/housing-loan/housing-loan-from-hdb
 *
 * Verified on: 25/09/2026. The rate is revised quarterly, so re-check it in
 * January, April, July and October.
 */
export const HDB_LOAN = {
  interestRate: 2.6,
  maxLtvPercent: 75,
  maxTenureYears: 25,
  msrPercent: 30,
  rateQuarter: "1 July to 30 September 2026",
  checkedOn: "25/09/2026",
} as const;

/**
 * Bank loan caps for the calculator's sliders. A bank can lend up to 75% on a
 * first housing loan, and the hard tenure stop is 35 years for private property
 * and 30 years for an HDB flat. Source: MAS Notice 632.
 * Verified on: 20/08/2026.
 */
export const BANK_LOAN = {
  maxTenureYears: 35,
} as const;

export const REFERENCE_LINKS = {
  bsd: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/buyer's-stamp-duty-(bsd)",
  absd: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/additional-buyer's-stamp-duty-(absd)",
  masPropertyLoans: "https://www.mas.gov.sg/regulation/notices/notice-632",
  // HDB rebuilt its site in 2026; the old /residential/... path now serves a
  // "We have moved" stub.
  hdbLoan:
    "https://www.hdb.gov.sg/buying-a-flat/flat-grant-and-loan-eligibility/housing-loan/housing-loan-from-hdb",
  hdbInterestRate:
    "https://www.hdb.gov.sg/managing-my-home/finances/loan-matters/interest-rate",
  cpfHousingUsage:
    "https://www.cpf.gov.sg/member/infohub/educational-resources/how-much-cpf-savings-you-can-use-for-your-home-purchase",
} as const;
